import React, { useState, useCallback, useEffect } from 'react';
import Layout from '@/components/Layout';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2, AlertTriangle, XCircle, RefreshCw,
  Database, Globe, Server, Clock, User as UserIcon
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  ok:      { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', Icon: CheckCircle2, label: 'Tudo OK' },
  warning: { color: 'bg-amber-100  text-amber-800  border-amber-200',    Icon: AlertTriangle, label: 'Atenção' },
  error:   { color: 'bg-red-100    text-red-800    border-red-200',      Icon: XCircle,       label: 'Erro' },
};

function StatusBadge({ status, label }) {
  const cfg = STATUS_STYLES[status] || STATUS_STYLES.warning;
  const Icon = cfg.Icon;
  return (
    <Badge className={`${cfg.color} border gap-1.5 px-2.5 py-1 font-medium`}>
      <Icon className="w-3.5 h-3.5" />
      {label || cfg.label}
    </Badge>
  );
}

function SectionCard({ icon: Icon, title, status, children, testId }) {
  return (
    <Card className="p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-gray-900">
          <Icon className="w-5 h-5 text-emerald-700" />
          <h3 className="font-semibold">{title}</h3>
        </div>
        {status && <StatusBadge status={status} />}
      </div>
      <div className="text-sm text-gray-700 space-y-1.5">{children}</div>
    </Card>
  );
}

function KV({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-gray-500 shrink-0">{label}:</span>
      <span className={`text-gray-900 break-all ${mono ? 'font-mono text-xs' : ''}`}>
        {value === null || value === undefined || value === '' ? '—' : String(value)}
      </span>
    </div>
  );
}

function HealthCheck({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/health/diagnostics');
      setData(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Erro desconhecido';
      setError(detail);
      toast.error('Falha ao buscar diagnóstico: ' + detail);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { run(); }, [run]);

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Diagnóstico do Sistema</h1>
            <p className="text-gray-600 mt-1">
              Verificação de saúde do backend, banco de dados, configuração de CORS e
              agendador. Útil para checar deployments após cada release.
            </p>
          </div>
          <Button
            onClick={run}
            disabled={loading}
            className="bg-emerald-700 hover:bg-emerald-800"
            data-testid="btn-run-health"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Verificando...' : 'Verificar Agora'}
          </Button>
        </div>

        {/* Overall banner */}
        {data && (
          <Card
            className={`p-5 border-2 ${
              data.overall === 'ok' ? 'border-emerald-300 bg-emerald-50' :
              data.overall === 'warning' ? 'border-amber-300 bg-amber-50' :
              'border-red-300 bg-red-50'
            }`}
            data-testid="health-overall"
          >
            <div className="flex items-center gap-3">
              <StatusBadge status={data.overall} />
              <span className="text-gray-700">
                Verificação concluída em <strong>{data.duration_ms}ms</strong> às{' '}
                {new Date(data.checked_at).toLocaleString('pt-BR')}
              </span>
            </div>
            {data.warnings?.length > 0 && (
              <ul className="mt-3 space-y-1 text-sm text-amber-900">
                {data.warnings.map((w, i) => (
                  <li key={`warning-${i}-${w.slice(0, 20)}`} className="flex gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        {error && (
          <Card className="p-5 border-2 border-red-300 bg-red-50">
            <div className="flex items-center gap-2 text-red-800">
              <XCircle className="w-5 h-5" />
              <strong>Não foi possível obter o diagnóstico</strong>
            </div>
            <p className="mt-2 text-sm text-red-700">{error}</p>
          </Card>
        )}

        {data && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard
              icon={Server}
              title="Servidor"
              status="ok"
              testId="card-server"
            >
              <KV label="Versão" value={data.server.app_version} />
              <KV label="Python" value={data.server.python} mono />
              <KV label="Plataforma" value={data.server.platform} mono />
              <KV label="Hora do servidor" value={new Date(data.server.now).toLocaleString('pt-BR')} />
            </SectionCard>

            <SectionCard
              icon={Database}
              title="Banco de Dados"
              status={data.database.ok ? 'ok' : 'error'}
              testId="card-database"
            >
              <KV label="Conexão" value={data.database.ok ? 'Conectado' : 'Falha'} />
              {data.database.error && <KV label="Erro" value={data.database.error} mono />}
              <div className="pt-2 border-t mt-2">
                <p className="text-xs text-gray-500 mb-1">Contagem de coleções:</p>
                {Object.entries(data.database.collections || {}).map(([k, v]) => (
                  <KV key={k} label={k} value={v} mono />
                ))}
              </div>
            </SectionCard>

            <SectionCard
              icon={Globe}
              title="CORS"
              status={
                data.cors.has_wildcard ? 'warning' :
                data.cors.is_safe_for_credentials ? 'ok' : 'warning'
              }
              testId="card-cors"
            >
              <KV label="Origens permitidas" value={data.cors.raw} mono />
              {data.cors.has_wildcard && (
                <p className="text-amber-700 text-xs mt-2 leading-relaxed">
                  Wildcard <code className="bg-amber-100 px-1 rounded">*</code> está
                  configurado. Funciona apenas porque o frontend NÃO usa{' '}
                  <code className="bg-amber-100 px-1 rounded">withCredentials</code>.
                  Para produção recomenda-se setar a env{' '}
                  <code className="bg-amber-100 px-1 rounded">CORS_ORIGINS</code> com
                  o domínio específico (ex: <code>https://quatrofolhas.app</code>).
                </p>
              )}
            </SectionCard>

            <SectionCard
              icon={Clock}
              title="Agendador (APScheduler)"
              status={data.scheduler.ok ? 'ok' : 'warning'}
              testId="card-scheduler"
            >
              <KV
                label="Última execução de prioridades"
                value={
                  data.scheduler.last_priority_run
                    ? new Date(data.scheduler.last_priority_run).toLocaleString('pt-BR')
                    : 'Nenhuma execução registrada ainda'
                }
              />
              {data.scheduler.error && <KV label="Erro" value={data.scheduler.error} mono />}
              <p className="text-xs text-gray-500 mt-2">
                Job diário roda às 07:00 (horário do servidor).
              </p>
            </SectionCard>

            <SectionCard
              icon={UserIcon}
              title="Sua Sessão"
              status="ok"
              testId="card-user"
            >
              <KV label="Email" value={data.user.email} />
              <KV label="Role" value={data.user.role} />
              <KV label="User ID" value={data.user.user_id} mono />
            </SectionCard>

            <SectionCard
              icon={Globe}
              title="Requisição"
              status="ok"
              testId="card-request"
            >
              <KV label="Origin" value={data.request.origin} mono />
              <KV label="Host" value={data.request.host} mono />
              <KV label="X-Forwarded-For" value={data.request.x_forwarded_for} mono />
            </SectionCard>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default HealthCheck;
